/**
 * Controlli del modulo Impostazioni dell'evento, prima di salvare: cosi' chi
 * compila legge "l'indirizzo deve iniziare con https://" invece di un errore
 * del database.
 */

import type { ImpostazioniEvento } from './tipi';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Testo vuoto -> null, altrimenti il testo senza spazi ai bordi. */
export const testoONull = (valore: string | null | undefined): string | null => {
  const pulito = (valore ?? '').trim();
  return pulito === '' ? null : pulito;
};

function indirizzoValido(valore: string): boolean {
  if (!valore.startsWith('https://')) return false;
  try {
    return Boolean(new URL(valore).hostname);
  } catch {
    return false;
  }
}

/** Ripulisce i campi di testo (vuoto = non impostato). */
export function normalizzaImpostazioni(i: ImpostazioniEvento): ImpostazioniEvento {
  return {
    ...i,
    nome: i.nome.trim(),
    url_scelta: testoONull(i.url_scelta),
    url_app: testoONull(i.url_app),
    email_riferimento: testoONull(i.email_riferimento),
    url_registrazione: testoONull(i.url_registrazione),
    prodotti_sito: [...new Set(i.prodotti_sito ?? [])].sort((a, b) => a - b),
  };
}

export type EsitoProdotti = { ok: true; prodotti: number[] } | { ok: false };

/**
 * I numeri dei prodotti scritti a mano ("1022, 1023"): separati da virgole o
 * spazi, solo cifre. Vuoto = nessun prodotto.
 */
export function leggiProdotti(testo: string): EsitoProdotti {
  const pezzi = testo.split(/[\s,;]+/).filter(Boolean);
  if (!pezzi.every((p) => /^\d{1,9}$/.test(p))) return { ok: false };
  const numeri = pezzi.map(Number).filter((n) => n > 0);
  return numeri.length === pezzi.length ? { ok: true, prodotti: [...new Set(numeri)].sort((a, b) => a - b) } : { ok: false };
}

/** I problemi da sistemare prima di salvare; vuoto se va tutto bene. */
export function validaImpostazioni(i: ImpostazioniEvento): string[] {
  const n = normalizzaImpostazioni(i);
  const errori: string[] = [];
  if (!n.nome) errori.push("Dai un nome all'evento.");
  if (n.url_scelta && !indirizzoValido(n.url_scelta)) {
    errori.push("L'indirizzo della pagina di scelta deve iniziare con https://");
  }
  if (n.url_app && !indirizzoValido(n.url_app)) errori.push("L'indirizzo dell'app deve iniziare con https://");
  if (n.email_riferimento && !EMAIL.test(n.email_riferimento)) errori.push("L'email di riferimento non sembra valida.");
  if (n.url_registrazione && !indirizzoValido(n.url_registrazione)) {
    errori.push("L'indirizzo della pagina dei biglietti deve iniziare con https://");
  }
  if (n.prodotti_sito.length > 0 && !n.url_registrazione) {
    errori.push("Con i prodotti del sito serve anche l'indirizzo della pagina dei biglietti.");
  }
  if (n.prodotti_sito.length > 0 && !n.lista_id) {
    errori.push('Con i prodotti del sito serve la lista dei partecipanti: chi registra un biglietto entra lì.');
  }
  const etichette = [
    n.etichetta_richiesta_id,
    n.etichetta_assegnata_id,
    n.etichetta_rimanda_link_id,
    n.etichetta_acquisto_id,
    n.etichetta_registrati_id,
  ].filter(Boolean);
  if (new Set(etichette).size !== etichette.length) {
    errori.push('Le etichette automatiche devono essere diverse fra loro.');
  }
  if (n.lista_id && etichette.includes(n.lista_id)) {
    errori.push("La lista dei partecipanti non puo' essere anche un'etichetta automatica.");
  }
  return errori;
}

export type EsitoPosti = { ok: true; posti: number | null } | { ok: false };

/** Legge i posti scritti a mano: vuoto = senza limite, altrimenti un intero da 0 in su. */
export function leggiPosti(testo: string): EsitoPosti {
  const pulito = testo.trim();
  if (pulito === '') return { ok: true, posti: null };
  if (!/^\d+$/.test(pulito)) return { ok: false };
  const posti = Number(pulito);
  return Number.isSafeInteger(posti) ? { ok: true, posti } : { ok: false };
}
